import { SchedulePreferencesResponse } from "../contracts/SchedulePreferencesResponse";
import { UpdateSchedulePreferencesRequest } from "../contracts/UpdateSchedulePreferencesRequest";
import { WorkingHoursMode } from "../contracts/WorkingHoursMode";
import { SchedulePreferences } from "../models/SchedulePreferences";

const toHHMM = (timeSpan: string): string => {
  if (!timeSpan) return "";
  const [h = "00", m = "00"] = timeSpan.split(":");
  return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
};

const toTimeSpan = (hhmm: string): string => {
  if (!hhmm) return "00:00:00";
  const parts = hhmm.split(":");
  const h = (parts[0] ?? "00").padStart(2, "0");
  const m = (parts[1] ?? "00").padStart(2, "0");
  return `${h}:${m}:00`;
};

/**
 * A profile that has never saved the granular per-day format carries its window
 * only in the legacy start/end fields — synthesize a SameEveryDay window from
 * them so working-hours checks don't treat every day as non-working.
 */
const legacyFallbackWindow = (response: SchedulePreferencesResponse) => {
  const start = toHHMM(response.startWorkingHours);
  const end = toHHMM(response.endWorkingHours);
  return start && end && start !== end ? [{ dayOfWeek: null, start, end }] : [];
};

export const mapResponseToPreferences = (response: SchedulePreferencesResponse): SchedulePreferences => {
  const hasGranularHours = response.workingHours.length > 0;
  return {
    workingHoursMode: hasGranularHours
      ? response.workingHoursMode
      : WorkingHoursMode.SameEveryDay,
    workingHours: hasGranularHours
      ? response.workingHours.map((w) => ({
          dayOfWeek: w.dayOfWeek,
          start: toHHMM(w.start),
          end: toHHMM(w.end),
        }))
      : legacyFallbackWindow(response),
    defaultDurationMinutes: response.defaultDurationMinutes,
    currency: response.currency,
    calendarInvitesEnabled: response.calendarInvitesEnabled,
    memberScheduleEmailsEnabled: response.memberScheduleEmailsEnabled,
    weekStart: response.weekStart,
  };
};

export const mapPreferencesToRequest = (preferences: SchedulePreferences): UpdateSchedulePreferencesRequest => ({
  workingHoursMode: preferences.workingHoursMode,
  workingHours: preferences.workingHours.map((w) => ({
    dayOfWeek: w.dayOfWeek,
    start: toTimeSpan(w.start),
    end: toTimeSpan(w.end),
  })),
  defaultDurationMinutes: preferences.defaultDurationMinutes,
  currency: preferences.currency,
  calendarInvitesEnabled: preferences.calendarInvitesEnabled,
  memberScheduleEmailsEnabled: preferences.memberScheduleEmailsEnabled,
  weekStart: preferences.weekStart,
});
